/**
 * A Review item's evidence, read the same way by tracker-api (review/evidence.ts: the item's evidence_fields, AIC-384)
 * and the analyst portal (screens/ReviewEvidence.tsx: the confirmation timeline), so the two can't drift.
 * Pure and dependency-free: the portal bundles it.
 */

/** One explicitly formatted event in an item's evidence: `<field_id> confirmed|corrected to <value> [usd_m|percent] at <hh:mm:ss[.fff]Z>`. */
interface EvidenceEvent { field: string; value: string; unit: string | null; action: string; time: string }

/** Only explicitly formatted events are extracted. Unmatched prose remains in the original evidence. */
export function evidenceEvents(text: string): EvidenceEvent[] {
  const events: EvidenceEvent[] = [];
  const pattern = /\b([a-z][a-z_]+:\d{4}(?:H[12])?(?::[^;\n]+?)?)\s+(confirmed|corrected to)\s+(-?\d+(?:\.\d+)?)(?:\s+(usd_m|percent))?\s+at\s+((?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?Z)\b/g;
  for (const match of text.matchAll(pattern)) events.push({ field: match[1]!, action: match[2]!, value: match[3]!, unit: match[4] ?? null, time: match[5]! });
  return events;
}

/** The field ids an item's evidence names (its events' fields), in order, each once. */
export const evidenceFieldIds = (text: string | null | undefined): string[] => [...new Set(evidenceEvents(text ?? "").map((e) => e.field))];

const HALF = /^(sw|svc|hw|iaas|other)_rev_total:(\d{4})H1$/;

/** The prior full year a first-half revenue total is compared with ("sw_rev_total:2026H1" → "sw_rev_total:2025"), else null. */
export function priorYearOf(fieldId: string): string | null {
  const m = HALF.exec(fieldId);
  return m ? `${m[1]}_rev_total:${Number(m[2]) - 1}` : null;
}

interface Comparable { field_id: string; unit: string | null; vendor_value: unknown }

/**
 * Each first-half revenue total paired with the same interview's prior full year, and the half as a percentage of the
 * year: both in usd_m, both finite numbers, the year's above zero. Anything else is not compared.
 */
export function revenueComparisons<F extends Comparable>(fields: readonly F[]): { half: F; previous: F; ratio: number }[] {
  return fields.flatMap((half) => {
    const prior = priorYearOf(half.field_id);
    if (!prior || half.unit !== "usd_m" || typeof half.vendor_value !== "number" || !Number.isFinite(half.vendor_value)) return [];
    const previous = fields.find((f) => f.field_id === prior);
    if (!previous || previous.unit !== half.unit || typeof previous.vendor_value !== "number" || !Number.isFinite(previous.vendor_value) || previous.vendor_value <= 0) return [];
    return [{ half, previous, ratio: half.vendor_value / previous.vendor_value * 100 }];
  });
}
